import { useDebounce } from '@uidotdev/usehooks';
import React, { useCallback, useEffect, useState } from 'react';

import {
  Button,
  Caption,
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Stack,
  Text,
} from '@eluve/components';
import { pluralize } from '@eluve/utils';

import { useBulkAddOrganizationsToBundle } from './useBulkAddOrganizationsToBundle';

const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 1;
// Mirrors the `limit: 100` on searchOrganizationsForBundleQuery.
const RESULT_CAP = 100;

interface AddOrganizationsToBundleDialogProps {
  bundleId: string;
}

/**
 * Presentational shell over {@link useBulkAddOrganizationsToBundle}: a Command
 * multi-select of organizations with a server-side debounced search, a
 * "select all matching" affordance, and a single bulk-add action. All logic
 * lives in the hook; this component only renders it.
 */
export const AddOrganizationsToBundleDialog: React.FC<
  AddOrganizationsToBundleDialogProps
> = ({ bundleId }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);

  const {
    searchOrganizations,
    results,
    isSearching,
    selected,
    toggle,
    selectAllMatching,
    clear,
    submit,
    isSubmitting,
  } = useBulkAddOrganizationsToBundle({
    bundleId,
    onComplete: () => setOpen(false),
  });

  // Debounced server-side search (filtering is server-side, so the Command
  // itself never filters). A term below the minimum length runs an empty
  // search, which the hook treats as a clear so a stale list can never be
  // swept up by "select all matching".
  useEffect(() => {
    const term =
      debouncedSearchTerm.trim().length < MIN_SEARCH_LENGTH
        ? ''
        : debouncedSearchTerm;
    void searchOrganizations(term);
  }, [debouncedSearchTerm, searchOrganizations]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) {
        setSearchTerm('');
        clear();
      }
    },
    [clear],
  );

  const hasSearched = searchTerm.trim().length >= MIN_SEARCH_LENGTH;
  const selectedCount = selected.size;
  const emptyMessage = isSearching
    ? 'Searching…'
    : hasSearched
      ? 'No organizations found'
      : 'Type to search organizations';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button
        variant="outline"
        size="sm"
        aria-label="Add organizations to this bundle"
        data-testid="add-organizations-trigger"
        onClick={() => setOpen(true)}
      >
        <Icon name="Plus" size="auto" />
        Add Organizations
      </Button>
      <DialogContent size="xl" data-testid="add-organizations-dialog">
        <DialogHeader>
          <DialogTitle>Add Organizations</DialogTitle>
          <DialogDescription>
            Search organizations and enable this bundle for all of them at once.
            Organizations already using this bundle are hidden.
          </DialogDescription>
        </DialogHeader>

        <Command shouldFilter={false} className="border border-border">
          <CommandInput
            value={searchTerm}
            onValueChange={setSearchTerm}
            placeholder="Search organizations by name…"
          />
          <CommandList>
            {results.length === 0 ? (
              <div className="py-6 text-center">
                <Text size="sm" color="tertiary">
                  {emptyMessage}
                </Text>
              </div>
            ) : (
              results.map((org) => (
                <CommandItem
                  key={org.id}
                  value={org.id}
                  onSelect={() => toggle(org)}
                  data-checked={selected.has(org.id) ? 'true' : undefined}
                >
                  {org.name}
                </CommandItem>
              ))
            )}
          </CommandList>
        </Command>

        <Stack direction="horizontal" align="center" justify="between" gap={2}>
          <Stack direction="horizontal" align="center" gap={3}>
            <Button
              variant="link"
              size="sm"
              onClick={selectAllMatching}
              disabled={results.length === 0}
            >
              Select all matching
            </Button>
            <Text size="xs" color="tertiary">
              Selected ({selectedCount})
            </Text>
            {selectedCount > 0 && (
              <Button variant="link" size="sm" onClick={clear}>
                Clear
              </Button>
            )}
          </Stack>
          {results.length >= RESULT_CAP && (
            <Caption color="tertiary">
              Showing first {RESULT_CAP} — refine your search to narrow
            </Caption>
          )}
        </Stack>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={selectedCount === 0 || isSubmitting}
            data-testid="add-organizations-submit"
          >
            {isSubmitting
              ? 'Adding…'
              : `Add to ${pluralize(selectedCount, 'organization')}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
